import { OrderItem } from '@/types/order-list';
import { cn } from '@/utils/tailwind';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Cell, Row, Table } from '@tanstack/react-table';
import { DragAlongCell } from './DragAlongCell';
import { deepEqual } from '@/utils/deep-equal';
import { TABLE_CHECKBOX_COLUMN_ID } from './tableConstants';

const getOrderNo = (rowData: object) => {
  if (!('orderNo' in rowData) || typeof rowData.orderNo !== 'string') {
    return '';
  }

  return rowData.orderNo;
};

interface TableBodyProps<TData extends object> {
  table: Table<TData>;
  checkedOrders?: OrderItem[];
  columnOrder: string[];
  /** 행 클릭 시 그 행의 체크박스를 토글한다(버튼/링크/체크박스 직접 클릭은 제외). */
  enableRowClickSelect?: boolean;
}

// 행 클릭 시 무시할 인터랙티브 요소(버튼/링크/입력/체크박스, 팝오버/드롭다운 트리거 등).
// [aria-haspopup]: Radix 트리거가 asChild div 여도(택배사 선택 등) 걸리게 한다.
const ROW_CLICK_IGNORE_SELECTOR =
  'button, a, input, select, textarea, label, [role="checkbox"], [role="button"], [role="link"], [aria-haspopup]';

export const TableBody = <TData extends object>({
  table,
  checkedOrders,
  columnOrder,
  enableRowClickSelect = false,
}: TableBodyProps<TData>) => {
  const rows = table.getRowModel().rows;

  // ✅ 주문번호 그룹별 인덱스를 계산
  const groupMap = new Map<string, number>();
  let groupIndex = 0;
  rows.forEach((row) => {
    const orderNo = getOrderNo(row.original);
    if (!groupMap.has(orderNo)) {
      groupMap.set(orderNo, groupIndex++);
    }
  });

  return (
    <tbody>
      {table.getRowModel().rows.map((row: Row<TData>) => {
        const order = row.original as OrderItem;

        const orderNo = getOrderNo(row.original);
        const group = groupMap.get(orderNo) ?? 0;
        const isEven = group % 2 === 0;

        // 체크박스 여부: prop이 없으면 무조건 false
        const isChecked = checkedOrders?.some((o) => deepEqual(o, order)) ?? false;
        return (
          <tr
            key={row.id}
            className={cn(
              'group h-[60px] max-h-[60px]  cursor-pointer border-b border-gr-200  transition-colors duration-300',
              // 좌측 sticky 체크박스 셀이 inherit 배경으로 가림 처리되려면 모든 행이 불투명해야 한다.
              isEven ? 'bg-gr-50' : 'bg-white',
              'hover:bg-[#f3f3f9]',
              isChecked && 'bg-point-100',
            )}
            onClick={
              enableRowClickSelect
                ? (event) => {
                    const target = event.target as HTMLElement;
                    // 포털(택배사 팝오버 등) 내부 클릭은 React 트리로는 행까지 버블되지만
                    // DOM 상으로는 행 밖이다. 행 DOM 내부 클릭만 행 선택으로 처리한다.
                    if (!event.currentTarget.contains(target)) return;
                    // 버튼/링크/체크박스 직접 클릭은 그 요소의 동작에 맡긴다.
                    if (target.closest(ROW_CLICK_IGNORE_SELECTOR)) return;
                    // 행 내부의 체크박스를 찾아 토글(없는 행=선택 불가/묶음행은 무동작).
                    const checkbox =
                      event.currentTarget.querySelector<HTMLElement>('[role="checkbox"]');
                    checkbox?.click();
                  }
                : undefined
            }
          >
            {/* 체크박스 셀 */}
            {row
              .getVisibleCells()
              .filter((cell) => cell.column.id === TABLE_CHECKBOX_COLUMN_ID)
              .map((cell: Cell<TData, unknown>) => (
                <DragAlongCell
                  key={cell.id}
                  order={order}
                  cell={cell}
                  disableDrag
                  checkedOrders={checkedOrders} // ✅ checkedOrders 전달
                />
              ))}

            {/* 나머지 셀 */}
            <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
              {row
                .getVisibleCells()
                .filter((cell) => cell.column.id !== TABLE_CHECKBOX_COLUMN_ID)
                .map((cell: Cell<TData, unknown>) => (
                  <DragAlongCell key={cell.id} cell={cell} />
                ))}
            </SortableContext>
          </tr>
        );
      })}
    </tbody>
  );
};
